import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { initializeAuth as initAuth } from '@/lib/auth';

// Font loading consolidated to index.css Google Fonts CDN for better performance

import './index.css';
import './styles/edge-styles.css';

// Handle chunk loading errors more gracefully
let chunkErrorCount = 0;
const MAX_CHUNK_RETRIES = 3;
let isReloading = false;

const isChunkError = (message: string) => {
  return (
    message?.includes('Loading chunk') ||
    message?.includes('Failed to fetch dynamically imported module') ||
    message?.includes('ChunkLoadError') ||
    message?.includes('Loading CSS chunk')
  );
};

const handleChunkError = (errorType: string) => {
  if (isReloading) return;

  chunkErrorCount++;
  console.warn(
    `Chunk loading error detected (${errorType}): ${chunkErrorCount}/${MAX_CHUNK_RETRIES}`
  );

  if (chunkErrorCount >= MAX_CHUNK_RETRIES) {
    isReloading = true;

    // Try to clear caches first
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    // Force reload with cache bypass
    setTimeout(() => {
      window.location.href = window.location.href + '?refresh=' + Date.now();
    }, 100);
  } else {
    // Retry after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }
};

window.addEventListener('error', (event) => {
  if (isChunkError(event.message || '')) {
    handleChunkError('error event');
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (isChunkError(event.reason?.message || '')) {
    event.preventDefault();
    handleChunkError('promise rejection');
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Initialize auth state
initAuth().then(() => {
  createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>
  );
});
